// PRD 14 I5 (stories 43–50) — one legacy family in Tenis10 Claims: the legacy family record (the tenis10.ro account's name,
// phone and address — admins only, kept for good), the migration invitation's steps, every child with what support may do
// (the support Claim, the hide, the cancel, the move, the show-again) and the family's own activity log; the family link
// resent from the header. The I0 simulation's cp-family, cp-family-claimed, cp-family-hidden and cp-family-archived.
// RELEASE-TENIS10 § 11 P55 (Tudor, 2026-10-25): beside the resend, the family link sent to a new email, and — under the
// family's name — where every claim email of the family goes (the tenis10.ro address, or the new one support set).
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { getFamily } from "./api"
import ChildrenTable from "./ChildrenTable"
import { ClaimsLogTable, useClaimLogs } from "./ClaimsLog"
import SupportActionDialog from "./SupportActionDialog"
import { accountLabel, childNumber, errorText, formatDate, formatDateTime, legacyIdText, refusalFor } from "./claimsLabels"
import LegacyId from "./LegacyId"
import "../../assets/scss/custom/pages/_tenis10Claims.scss"

const WAVES = { active: "Wave 1", under16: "Wave 2", adultOnly: "Wave 3" }
const LINK_STATES = { valid: "valid", expired: "expired", revoked: "replaced" }

const Field = ({ label, children }) => <div className="mb-3"><div className="text-muted font-size-12">{label}</div><div className="font-weight-medium">{children}</div></div>

const DAY = 24 * 3600 * 1000
/** Whether the family's emails can leave: its address (P55: the new one support set, once set) can be mailed and did not bounce. */
const reachable = family => (family.delivery ? !family.delivery.bounce : family.contact.emailValid && !family.bounce)

/** The next reminder (+7, then +30 days from the first email) while one can still leave: sent, mailable, not bounced, not
 * archived, a child under 16 still unclaimed. */
export function nextReminder(family) {
  const steps = new Set(family.reminders.map(reminder => reminder.step))
  const waiting = family.children.some(child => !child.adult && child.state === "unclaimed")
  if (!family.sentAt || !reachable(family) || family.archived || !waiting || steps.has("d30")) return null
  const days = steps.has("d7") ? 30 : 7
  return { step: `+${days}`, at: new Date(new Date(family.sentAt).getTime() + days * DAY) }
}

/** The migration invitation's steps, as a timeline: [icon, title, detail]. */
export function invitationSteps(family) {
  const steps = []
  steps.push(family.sentAt
    ? ["bx-send text-info", `Sent with ${(WAVES[family.wave] || "its wave").toLowerCase()} · ${formatDateTime(family.sentAt)}`, "Accepted by the mail server. This does not confirm delivery to the inbox."]
    : ["bx-time muted", "Not sent yet", "The family's email leaves with its wave."])
  if (family.bounce) {
    steps.push(["bx-error-circle text-danger", `Bounce · ${family.bounce.kind} · ${formatDate(family.bounce.at)}`, family.delivery
      ? "The tenis10.ro address rejected the email. The family's emails go to the new address now."
      : "The address rejected the email: send the link to a new email the parent gives you, or claim the children from here."])
  }
  // RELEASE-TENIS10 § 11 P55 — the family link support sent to a new email: every email of the family goes there since
  if (family.delivery) {
    const by = family.delivery.by?.name ? ` by ${family.delivery.by.name}` : ""
    steps.push(["bx-envelope text-primary", `Link sent to a new email · ${formatDateTime(family.delivery.at)}`,
      `To ${family.delivery.email}${by}. Every email of the family goes there from now on.`])
    if (family.delivery.bounce) {
      steps.push(["bx-error-circle text-danger", `Bounce of the new email · ${family.delivery.bounce.kind} · ${formatDate(family.delivery.bounce.at)}`,
        "The new address rejected the email: ask the parent for another address."])
    }
  }
  if (family.sentAt) {
    steps.push(family.openedAt
      ? ["bx-envelope-open text-primary", `Opened · ${formatDateTime(family.openedAt)}`, "The claim page was opened from the family link."]
      : ["bx-envelope muted", "Not opened yet", "Nobody opened the claim page from the family link."])
  }
  family.reminders.forEach(reminder => steps.push(["bx-bell text-warning", `Reminder ${reminder.step === "d7" ? "+7" : "+30"} sent · ${formatDate(reminder.at)}`, "Sent while a child under 16 was still unclaimed."]))
  const next = nextReminder(family)
  if (next) steps.push(["bx-bell muted", `Next reminder: ${next.step} · ${formatDate(next.at)}`, "Only while a child under 16 is still unclaimed."])
  steps.push(family.boundTo
    ? ["bx-link text-success", `Bound to account ${accountLabel(family.boundTo.account)} · ${formatDate(family.boundTo.at)}`, "The family's other children under 16 can be claimed through the link only into this account."]
    : ["bx-unlink muted", "Not bound to an account yet", "The first account that claims a child through the link becomes the family's account."])
  if (family.link) {
    steps.push([
      family.link.state === "valid" ? "bx-time-five text-success" : "bx-time-five text-muted",
      `Link ${LINK_STATES[family.link.state] || family.link.state} · until ${formatDate(family.link.expiresAt)}`,
      `New-link requests: ${family.requests.count} · 16+ forwards: ${family.forwards} · hidden by the family: ${family.children.filter(child => child.hidden?.by === "parent").length}`,
    ])
  }
  if (family.archived) steps.push(["bx-archive muted", "Archived", "Every child is claimed or hidden: the invitation is closed (no reminders, no new link). The data stays."])
  return steps
}

/** RELEASE-TENIS10 § 11 P55 (Tudor) — where every claim email of the family goes, said under its name: [address, where it comes from]. */
export function emailsTo(family) {
  if (family.delivery) {
    const by = family.delivery.by?.name ? ` (${family.delivery.by.name})` : ""
    return [family.delivery.email, `new address set by support${by} · ${formatDateTime(family.delivery.at)}`]
  }
  if (family.contact.email && family.contact.emailValid) return [family.contact.email, "the tenis10.ro address"]
  return [null, "the tenis10.ro address cannot be mailed"]
}

export default function FamilyPage() {
  const { invitationId } = useParams()
  const location = useLocation()
  const [family, setFamily] = useState({ status: "loading" })
  const [dialog, setDialog] = useState(null)
  const [banner, setBanner] = useState(null)
  const load = useCallback(() => {
    let current = true
    getFamily(invitationId)
      .then(data => current && setFamily({ status: "ready", data }))
      .catch(error => current && setFamily({ status: "error", message: errorText(error, "The family didn't load. Click Retry.") }))
    return () => { current = false }
  }, [invitationId])
  useEffect(load, [load])
  const data = family.data
  const logIds = useMemo(() => (data ? [data.invitationId, ...data.children.map(child => child.profileId)] : []), [data])
  const log = useClaimLogs(logIds)
  // RELEASE-TENIS10 § 11 P78: the log names a child with its number, as it names an account
  const childNames = useMemo(() => Object.fromEntries((data?.children || []).map(child => [child.profileId,
    `${child.firstName} ${child.lastName}${childNumber(child) != null ? ` #${childNumber(child)}` : ""}`])), [data])
  const back = `/accounts/tenis10-claims${location.state?.registerSearch || ""}`

  if (family.status !== "ready") {
    return <div className="page-content tenis10-claims"><Container fluid>
      <Breadcrumbs title="Tenis10 Claims" breadcrumbItem="Legacy family" path={back} />
      <Card><CardBody>{family.status === "loading"
        ? <div className="tenis10-claims-state" role="status"><Spinner size="sm" className="mr-2" />Loading the family…</div>
        : <div className="tenis10-claims-state" role="alert"><p>{family.message}</p><button type="button" className="btn btn-primary" onClick={load}>Retry</button></div>}
      </CardBody></Card>
    </Container></div>
  }

  const parentName = [data.contact.firstName, data.contact.lastName].filter(Boolean).join(" ") || "—"
  const under16 = data.children.filter(child => !child.adult).length
  const hiddenByParent = data.children.filter(child => child.hidden?.by === "parent")
  const [emailsAddress, emailsSource] = emailsTo(data)
  const emailsBounce = data.delivery ? data.delivery.bounce : data.bounce
  // P55 — the link sent to a new email (an API before P55 sends no state: the button stays off)
  const sendToEmail = data.sendToEmail || { allowed: false, reason: null }
  const sendWhy = sendToEmail.allowed || !sendToEmail.reason ? undefined
    : sendToEmail.reason === data.resend.reason && !data.resend.allowed ? "tenis10-claims-resend-why" : "tenis10-claims-send-to-email-why"
  const done = message => {
    setDialog(null)
    setBanner(message)
    load()
    log.load(1)
  }
  return <div className="page-content tenis10-claims"><Container fluid>
    <Breadcrumbs title="Tenis10 Claims" breadcrumbItem={`${legacyIdText(data.legacy.userId)} · Legacy family`} path={back} />
    {banner && <div className="alert alert-success py-2 px-3" role="status">{banner} The action and your note are in the activity log.</div>}
    {data.archived && <div className="alert alert-secondary py-2 px-3" role="status"><b>Archived</b> — every child of the family is claimed or hidden. The migration invitation is closed: no reminders, no new link. The legacy family record stays here for good.</div>}
    {hiddenByParent.map(child => <div key={child.profileId} className="alert alert-info py-2 px-3" role="status">
      <b>{child.firstName} {child.lastName} was hidden by the parent</b> — {formatDateTime(child.hidden.at)}, from the family link (“Nu vreau acest profil pe Sportya”). Nothing to do; the parent got the confirmation. Show the profile again only if the parent asks.
    </div>)}

    <Card><CardBody className="d-flex flex-wrap justify-content-between align-items-center tenis10-claims-header">
      <div>
        <h4 className="mb-0 font-size-16">{parentName} <span className="text-muted font-weight-normal">· tenis10.ro account <LegacyId id={data.legacy.userId} /></span>
          {data.archived && <span className="badge badge-soft-secondary ml-2">Archived</span>}</h4>
        <div className="text-muted font-size-12">{WAVES[data.wave] || data.wave} · {data.children.length} {data.children.length === 1 ? "child" : "children"}{under16 ? ` (${under16} under 16)` : ""}
          {data.boundTo ? ` · bound to account #${data.boundTo.account.userId}` : ""} · migration invitation in Romanian</div>
        <div className="tenis10-claims-emails-to font-size-13 mt-2" data-testid="family-emails-to">
          <i className="bx bx-envelope mr-1" aria-hidden="true" />Claim emails go to: <strong>{emailsAddress || "—"}</strong>
          <span className="text-muted"> · {emailsSource}</span>
          {emailsBounce && <span className="badge badge-soft-danger ml-2">Bounced</span>}
        </div>
      </div>
      <div className="tenis10-claims-header-actions">
        <div className="tenis10-claims-header-buttons">
          <button type="button" className="btn btn-primary" disabled={!data.resend.allowed} onClick={() => setDialog({ action: "resend" })}
            aria-describedby={data.resend.allowed ? undefined : "tenis10-claims-resend-why"}>Resend family link</button>
          <button type="button" className="btn btn-outline-primary" disabled={!sendToEmail.allowed} onClick={() => setDialog({ action: "sendToNewEmail" })}
            aria-describedby={sendWhy}>Send the link to a new email</button>
        </div>
        {!data.resend.allowed && <div id="tenis10-claims-resend-why" className="text-muted font-size-12 mt-1">{refusalFor(data.resend.reason)}</div>}
        {sendWhy === "tenis10-claims-send-to-email-why" && <div id="tenis10-claims-send-to-email-why" className="text-muted font-size-12 mt-1">{refusalFor(sendToEmail.reason)}</div>}
      </div>
    </CardBody></Card>

    <Row>
      <Col lg={5}><Card><CardBody>
        <div className="tenis10-claims-section-label">Legacy family record (from tenis10.ro)</div>
        <Field label="Name">{parentName}</Field>
        <Field label="Phone">{data.contact.phone || "—"}</Field>
        <Field label={data.delivery ? "Email on tenis10.ro (no longer receives the family's emails)" : "Email (the migration invitation's address)"}>{data.contact.email || "—"}
          {data.contact.email && !data.contact.emailValid && <span className="badge badge-soft-danger ml-2">Cannot be mailed</span>}</Field>
        <p className="tenis10-claims-explain mb-0">The legacy family record is kept for good in Tenis10 Claims and is visible only to admins — never erased, also after every child is claimed. Without the link, compare the name and the phone with what the parent tells you, and write what you checked in the note.</p>
      </CardBody></Card></Col>
      <Col lg={7}><Card><CardBody>
        <div className="tenis10-claims-section-label">Migration invitation</div>
        <ul className="tenis10-claims-timeline">{invitationSteps(data).map(([icon, title, detail]) => <li key={title}>
          <i className={`bx ${icon}`} aria-hidden="true" /><div><div className="font-weight-medium">{title}</div><div className="text-muted font-size-12">{detail}</div></div>
        </li>)}</ul>
      </CardBody></Card></Col>
    </Row>

    <Card><CardBody className="p-0">
      <div className="p-3 border-bottom">
        <h4 className="card-title mb-1">Children of the family <span className="badge badge-soft-secondary">{data.children.length}</span></h4>
        <p className="card-title-desc mb-0">Under 16: claimed into a parent account. 16+: claimed by the player (an account on the forwarded address), or by support into the player's account.</p>
      </div>
      <ChildrenTable rows={data.children} label="Children of the family" onAction={(action, child) => setDialog({ action, child })} />
    </CardBody></Card>

    <Card><CardBody className="p-0">
      <div className="p-3 border-bottom d-flex flex-wrap justify-content-between align-items-baseline">
        <h4 className="card-title mb-0">Activity log of the family</h4>
        <Link to="/accounts/tenis10-claims/log" className="font-size-13">All Tenis10 Claims actions</Link>
      </div>
      <ClaimsLogTable log={log} childNames={childNames} label="Activity log of the family" />
    </CardBody></Card>

    {dialog && <SupportActionDialog action={dialog.action} child={dialog.child} family={data} onClose={() => setDialog(null)} onDone={done} />}
  </Container></div>
}
